import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { getAppVersions } from 'services/analyticsService';
import { AppVersionsResponse } from 'types/analytics.types';

const AppVersions: React.FC = () => {
  const [data, setData] = useState<AppVersionsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [days, setDays] = useState(7);
  const [targetVersion, setTargetVersion] = useState('2.1.0');
  const [platform, setPlatform] = useState<'all' | 'ios' | 'android'>('all');

  const loadData = useCallback(async () => {
    setIsLoading(true);
    const result = await getAppVersions({
      days,
      targetVersion: targetVersion.trim() || undefined,
      platform: platform === 'all' ? undefined : platform,
    });
    setData(result);
    setIsLoading(false);
  }, [days, targetVersion, platform]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (isLoading) {
    return (
      <Box
        sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!data) {
    return (
      <Typography sx={{ p: 3, textAlign: 'center' }}>
        Unable to load app version analytics.
      </Typography>
    );
  }

  return (
    <Box sx={{ width: '100%', maxWidth: 960, mx: 'auto', p: { xs: 1, md: 3 } }}>
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
        App versions
      </Typography>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <TextField
          label="Window (days)"
          type="number"
          size="small"
          value={days}
          onChange={(e) => setDays(Math.max(1, parseInt(e.target.value, 10) || 7))}
          sx={{ width: 140 }}
        />
        <TextField
          label="Target version"
          size="small"
          value={targetVersion}
          onChange={(e) => setTargetVersion(e.target.value)}
          sx={{ width: 160 }}
        />
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Platform</InputLabel>
          <Select
            label="Platform"
            value={platform}
            onChange={(e) => setPlatform(e.target.value as 'all' | 'ios' | 'android')}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="ios">iOS</MenuItem>
            <MenuItem value="android">Android</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <Paper sx={{ p: 2, flex: '1 1 180px' }}>
          <Typography variant="body2" color="text.secondary">
            Active users ({data.windowDays}d)
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            {data.totalActiveUsers.toLocaleString()}
          </Typography>
        </Paper>
        {data.adoption && (
          <>
            <Paper sx={{ p: 2, flex: '1 1 180px' }}>
              <Typography variant="body2" color="text.secondary">
                On ≥ {data.adoption.targetVersion}
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 600 }}>
                {data.adoption.percentOnOrAbove}%
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {data.adoption.onOrAboveTarget.toLocaleString()} users
              </Typography>
            </Paper>
            <Paper sx={{ p: 2, flex: '1 1 180px' }}>
              <Typography variant="body2" color="text.secondary">
                Below target
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 600 }}>
                {data.adoption.belowTarget.toLocaleString()}
              </Typography>
            </Paper>
            <Paper sx={{ p: 2, flex: '1 1 180px' }}>
              <Typography variant="body2" color="text.secondary">
                Unknown version
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 600 }}>
                {data.unknownVersionUsers.toLocaleString()}
              </Typography>
            </Paper>
          </>
        )}
      </Box>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Version</TableCell>
              <TableCell>Platform</TableCell>
              <TableCell align="right">Count</TableCell>
              <TableCell align="right">%</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.byVersion.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  No version data in this window.
                </TableCell>
              </TableRow>
            ) : (
              data.byVersion.map((row) => (
                <TableRow key={`${row.appVersion}-${row.platform}`}>
                  <TableCell>{row.appVersion}</TableCell>
                  <TableCell sx={{ textTransform: 'capitalize' }}>{row.platform}</TableCell>
                  <TableCell align="right">{row.count.toLocaleString()}</TableCell>
                  <TableCell align="right">{row.percentOfActive}%</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
        As of {new Date(data.asOf).toLocaleString()}
      </Typography>
    </Box>
  );
};

export default AppVersions;
